import React, { useEffect, useContext, useState } from "react";
import { FirebaseContext } from "../lib/firebase";
import {
  query,
  collection,
  onSnapshot,
  orderBy,
  getDoc,
  doc,
} from "firebase/firestore";
import Modal from "react-modal";
import { Form, Field, Formik } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import { useRole } from "../lib/userRole";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    border: "1px solid #eee",
    background: "#fff",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    outline: "none",
    padding: "0",
  },
  overlay: { zIndex: 5 },
};

const detailModalStyles = {
  content: {
    border: "1px solid #eee",
    background: "#fff",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    outline: "none",
    padding: "0",
  },
  overlay: { zIndex: 5 },
};

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

function formatNumber(number) {
  let newNumber;
  if (number.length > 10) {
    newNumber = `+${number[0]} (${number[1]}${number[2]}${number[3]}) ${number[4]}${number[5]}${number[6]}-${number[7]}${number[8]}${number[9]}${number[10]}`;
  } else if (number.length === 10) {
    newNumber = `+1 (${number[0]}${number[1]}${number[2]}) ${number[3]}${number[4]}${number[5]}-${number[6]}${number[7]}${number[8]}${number[9]}`;
  } else {
    newNumber = "";
  }

  return newNumber;
}

const schema = Yup.object().shape({
  firstName: Yup.string()
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  lastName: Yup.string()
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  phoneNumber: Yup.string()
    .matches(phoneRegExp, "Phone number is not valid")
    .required("Required"),
  mobile: Yup.string().matches(phoneRegExp, "Phone number is not valid"),
  email: Yup.string().email().required("Required"),
  role: Yup.string().required("Required"),
  location: Yup.string().required("Required"),
  department: Yup.string().required("Required"),
  manager: Yup.string().required("Required"),
});

export default function EmployeeController({ data, first, forceRender }) {
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [employees, setEmployees] = useState([]);
  const [temp, setTemp] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [detailEmployee, setDetailEmployee] = useState({});
  const navigate = useNavigate();
  const { role } = useRole();

  const { api, database } = useContext(FirebaseContext);
  const [errors, setErrors] = useState([]);

  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [modalIsOpen, setIsOpen] = useState(false);
  const [detailModalIsOpen, setDetailModalIsOpen] = useState(false);
  const [confirmationIsOpen, setConfirmationIsOpen] = useState(false);
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);

  async function handleSearch() {
    if (inputValue) {
      setSearchInitiated(true);
      const documentList = await api.search(inputValue);

      const documents = await Promise.all(
        documentList.map((document) =>
          getDoc(doc(database, "employees", document.objectID))
        )
      );

      const docsData = await documents.map((docSnap) => {
        if (docSnap.exists()) {
          return { ...docSnap.data(), id: docSnap.id };
        } else {
          return null;
        }
      });

      // const q = query(
      //   collection(database, 'applicants'),
      //   where('searchTerms', 'array-contains', inputValue)
      // );
      // const querySnapshot = await getDocs(q);
      const fetchedApplicants = [];
      await docsData.forEach((doc) => {
        fetchedApplicants.push(doc);
      });
      setTemp(employees);
      setEmployees(fetchedApplicants);
    }
  }

  function handleClear() {
    setInputValue("");
    setSearchInitiated(false);
    setEmployees(temp);
  }

  function openModal() {
    setMessage("");
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  function openEditModal() {
    setEditModalIsOpen(true);
  }

  function closeEditModal() {
    setMessage("");
    setEditModalIsOpen(false);
  }

  function openDetailModal() {
    setDetailModalIsOpen(true);
  }

  function closeDetailModal() {
    setDetailModalIsOpen(false);
  }

  function toggleConfirmation() {
    setConfirmationIsOpen(!confirmationIsOpen);
  }

  async function handleDeleteError(id) {
    try {
      await api.removeError(id);
    } catch (error) {
      setErrorMessage(
        "An error occured when deleting the error. Please try again."
      );
    }
  }

  async function handleAdd(values) {
    let date = new Date().toUTCString().slice(5, 16);
    values.dateAdded = date;
    let timestamp = Date.now();
    values.timestamp = timestamp;
    let image;
    if (values.file) {
      image = values.file;
    }
    delete values.file;

    const response = await api.addEmployee(values, image);
    if (response.success) {
      closeModal();
    } else {
      setMessage("An error occured when adding employee. Please try again.");
    }
  }

  async function handleUpdate(values) {
    values.id = selectedEmployee.id;

    let image;
    if (typeof values.file !== "string") {
      image = values.file;
      delete values.file;
    }

    try {
      await api.updateEmployee(values, image);
      setSelectedEmployee({});
      closeEditModal();
    } catch (error) {
      setSelectedEmployee({});
      setMessage("An error occured when editing employee. Please try again.");
    }
  }

  async function handleDelete(id) {
    try {
      await api.removeEmployee(id);
      setSelectedEmployee({});
      toggleConfirmation();
    } catch (error) {
      setSelectedEmployee({});
      setMessage(
        "An error occured when deleting the employee. Please try again."
      );
    }
  }

  useEffect(() => {
    const q = query(
      collection(database, "employees"),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(q, (QuerySnapshot) => {
      const fetchedEmployees = [];
      QuerySnapshot.forEach((doc) => {
        fetchedEmployees.push({ ...doc.data(), id: doc.id });
      });

      setEmployees(fetchedEmployees);
    });

    return () => unsubscribe;
  }, [database]);

  useEffect(() => {
    const q = query(collection(database, "errors"));

    const unsubscribe = onSnapshot(q, (QuerySnapshot) => {
      const fetchedErrors = [];
      QuerySnapshot.forEach((doc) => {
        fetchedErrors.push({ ...doc.data(), id: doc.id });
      });
      const sortedErrors = fetchedErrors.sort(
        (a, b) => a.createdAt - b.createdAt
      );
      setErrors(sortedErrors);
    });

    return () => unsubscribe;
  }, [database]);

  return (
    <>
      <div className='errors fixed top-10 right-0 mr-10'>
        {errorMessage && (
          <div className='h-10 w-full z-10 mb-8'>
            <div className='text-white px-6 py-4 border-0 rounded relative bg-red-500'>
              <span className='text-xl inline-block mr-5 align-middle'>
                <i className='fas fa-bell' />
              </span>
              <span className='inline-block align-middle mr-8'>
                <b className='capitalize'>Oh Shoot!</b> {errorMessage}
              </span>
              <button
                onClick={() => setErrorMessage("")}
                className='absolute bg-transparent text-2xl font-semibold leading-none right-0 top-0 mt-4 mr-6 outline-none focus:outline-none'
              >
                <span>×</span>
              </button>
            </div>
          </div>
        )}
        {errors.map((error) => (
          <div className='h-10 w-full z-10 mb-8'>
            <div className='text-white px-6 py-4 border-0 rounded relative bg-red-300 flex flex-col'>
              <div>
                <span className='text-xl inline-block mr-5 align-middle'>
                  <i className='fas fa-bell' />
                </span>
                <span className='inline-block align-middle mr-8'>
                  <b className='capitalize'>Oh Shoot!</b> {error.details}
                </span>

                <button
                  onClick={() => handleDeleteError(error.id)}
                  className='absolute bg-transparent text-2xl font-semibold leading-none right-0 top-0 mt-4 mr-6 outline-none focus:outline-none'
                >
                  <span>×</span>
                </button>
              </div>
              {error.errors && (
                <span className='text-[10px] inline-block mr-5 align-middle'>
                  {JSON.stringify(error.errors)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className='px-4 sm:px-6 lg:px-8 mt-10'>
        <div className='mb-10'>
          <Link
            to={`/dashboard/`}
            className='rounded-md max-w-20 mb-10 bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800'
          >
            Back
          </Link>
        </div>
        <div className='flex w-full flex-row items-stretch max-w-[300px] mb-10 rounded-md border-gray-300 border-[1px] '>
          <span className='h-full leading-snug font-normal text-center bg-transparent rounded text-base items-center justify-center w-8 pl-3 py-3'>
            {/* <i className="fas fa-lock"></i> */}
            <MagnifyingGlassIcon className='h-6 w-6 text-gray-300' />
          </span>
          <input
            type='text'
            placeholder='search'
            className='px-3 py-3 relative bg-white rounded text-sm w-full outline-none'
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <button
            onClick={() => handleSearch()}
            className={`${
              searchInitiated ? "" : "rounded-r-md"
            } bg-rose-400 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-rose-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-400`}
          >
            Submit
          </button>
          {searchInitiated && (
            <button
              onClick={() => handleClear()}
              className='rounded-r-md bg-rose-300 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-rose-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-400'
            >
              Clear
            </button>
          )}
        </div>

        <div className='sm:flex sm:items-center mb-24'>
          <div className='flex justify-center lg:flex-auto lg:justify-start'>
            <h1 className='text-2xl font-[700] leading-loose md:text-3xl text-gray-900 sr-only'>
              Farm Equipment USA Internal
            </h1>
            <img src='/logo.png' alt='FEUSA logo'></img>
          </div>

          <div className='mt-4 sm:ml-16 sm:mt-0 flex flex-col justify-end'>
            {role.role === "admin" && (
              <>
                <button
                  onClick={() => openModal()}
                  className='block rounded-md bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800 mb-5'
                >
                  Add New Employee
                </button>
              </>
            )}

            <button
              className="className='block rounded-md bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-800'"
              onClick={() => {
                api.logout();
                navigate("/");
              }}
            >
              Logout
            </button>
          </div>
        </div>

        <div className='bg-white'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div className='mx-auto max-w-2xl lg:mx-0'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl'>
                Our staff
              </h2>
              <p className='mt-6 text-lg leading-8 text-gray-600'>
                We’re a dynamic group of individuals who are passionate about
                what we do and dedicated to helping the American farmer succeed
              </p>
            </div>
            <ul className='mx-auto mt-20 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3'>
              {employees
                .sort((a, b) => {
                  // Compare the first names
                  if (a.firstName < b.firstName) {
                    return -1;
                  }
                  if (a.firstName > b.firstName) {
                    return 1;
                  }
                  return 0; // names must be equal
                })
                .map((person) => (
                  <li
                    className='flex flex-col items-start'
                    key={`${person.firstName} + ${person.lastName}`}
                  >
                    <button
                      onClick={() => {
                        setDetailEmployee(person);
                        openDetailModal();
                      }}
                    >
                      {person.file ? (
                        <img
                          className='aspect-[3/2] w-full rounded-2xl object-cover hover:scale-[102%]'
                          src={person.file}
                          alt=''
                        />
                      ) : (
                        <img
                          className='aspect-[3/2] w-full rounded-2xl object-cover hover:scale-[102%]'
                          src='/avatar.png'
                          alt=''
                        />
                      )}
                    </button>

                    <h3 className='mt-6 text-lg font-semibold leading-8 tracking-tight text-gray-900'>
                      {person.firstName} {person.lastName}
                    </h3>
                    <p className='text-base leading-7 text-gray-600'>
                      {person.role}
                    </p>
                    <p>
                      <a
                        target='_blank'
                        rel='noopener noreferrer'
                        href={`tel:${person.phoneNumber}`}
                        className='text-base leading-7 text-gray-900
                        hover:text-gray-600'
                      >
                        {formatNumber(person.phoneNumber)}
                      </a>
                    </p>
                    {person.mobile && (
                      <p>
                        <a
                          target='_blank'
                          rel='noopener noreferrer'
                          href={`tel:${person.mobile}`}
                          className='text-base leading-7 text-gray-900
                        hover:text-gray-600'
                        >
                          {formatNumber(person.mobile)}
                        </a>
                      </p>
                    )}
                    <p>
                      <a
                        target='_blank'
                        rel='noopener noreferrer'
                        href={`mailto:${person.email}`}
                        className='text-base leading-7 text-gray-900 hover:text-gray-600'
                      >
                        {person.email}
                      </a>
                    </p>
                    <p className='text-xs leading-7 text-gray-600'>
                      Location:{" "}
                      <span className='text-sm text-gray-900'>
                        {person.location}
                      </span>
                    </p>
                    <p className='text-xs leading-7 text-gray-600'>
                      Department:{" "}
                      <span className='text-sm text-gray-900'>
                        {person.department}
                      </span>
                    </p>
                    <p className='text-xs leading-7 text-gray-600'>
                      Manager:{" "}
                      <span className='text-sm text-gray-900'>
                        {person.manager}
                      </span>
                    </p>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>

      {/* __________________________________________________________________ */}
      <Modal
        isOpen={modalIsOpen}
        // onAfterOpen={afterOpenModal}
        onRequestClose={closeModal}
        style={modalStyles}
        contentLabel='Add Modal'
      >
        <div className='bg-zinc-50 shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                Add Employee
              </h3>
              <button
                onClick={closeModal}
                className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
              >
                Cancel
              </button>
            </div>
            <div className='mt-2 max-w-xl text-sm text-gray-500'>
              <p>Manually add an Employee.</p>
            </div>
            <Formik
              initialValues={{
                firstName: "",
                lastName: "",
                phoneNumber: "",
                mobile: "",
                email: "",
                file: "",
                role: "",
                location: "",
                manager: "",
                department: "",
              }}
              validationSchema={schema}
              onSubmit={async (values, actions) => {
                await handleAdd(values);
                actions.setSubmitting(false);
              }}
            >
              {({ isSubmitting, setFieldValue, errors, touched, values }) => (
                <Form className='mt-5 sm:flex sm:items-center flex-col'>
                  <div className='flex flex-row'>
                    <div className='w-full sm:max-w-xs mr-2'>
                      <label
                        htmlFor='first-name'
                        className='block text-sm font-semibold leading-6'
                      >
                        First name
                      </label>
                      <Field
                        type='text'
                        name='firstName'
                        id='first-name'
                        autoComplete='given-name'
                        className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                      />
                      {errors.firstName && touched.firstName ? (
                        <div className='error'>{errors.firstName}</div>
                      ) : null}
                    </div>
                    <div className='w-full sm:max-w-xs'>
                      <label
                        htmlFor='last-name'
                        className='block text-sm font-semibold leading-6'
                      >
                        Last name
                      </label>
                      <Field
                        type='text'
                        name='lastName'
                        id='last-name'
                        autoComplete='family-name'
                        className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                      />
                      {errors.lastName && touched.lastName ? (
                        <div className='error'>{errors.lastName}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='email'
                      className='block text-sm font-semibold leading-6'
                    >
                      Email
                    </label>
                    <Field
                      type='email'
                      name='email'
                      id='email'
                      autoComplete='email'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.email && touched.email ? (
                      <div className='error'>{errors.email}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='phone-number'
                      className='block text-sm font-semibold leading-6'
                    >
                      Phone
                    </label>
                    <Field
                      type='tel'
                      name='phoneNumber'
                      id='phone-number'
                      autoComplete='tel'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.phoneNumber && touched.phoneNumber ? (
                      <div className='error'>{errors.phoneNumber}</div>
                    ) : null}
                  </div>

                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='phone-number'
                      className='block text-sm font-semibold leading-6'
                    >
                      Mobile
                    </label>
                    <Field
                      type='tel'
                      name='mobile'
                      id='mobile'
                      autoComplete='tel'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.mobile && touched.mobile ? (
                      <div className='error'>{errors.mobile}</div>
                    ) : null}
                  </div>

                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='role'
                      className='block text-sm font-semibold leading-6'
                    >
                      Role
                    </label>
                    <Field
                      type='text'
                      name='role'
                      id='role'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.role && touched.role ? (
                      <div className='error'>{errors.role}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='location'
                      className='block text-sm font-semibold leading-6'
                    >
                      Location
                    </label>
                    <Field
                      type='text'
                      name='location'
                      id='location'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.location && touched.location ? (
                      <div className='error'>{errors.location}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='department'
                      className='block text-sm font-semibold leading-6'
                    >
                      Department
                    </label>
                    <Field
                      type='text'
                      name='department'
                      id='department'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.department && touched.department ? (
                      <div className='error'>{errors.department}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='manager'
                      className='block text-sm font-semibold leading-6'
                    >
                      Manager
                    </label>
                    <Field
                      type='text'
                      name='manager'
                      id='manager'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.manager && touched.manager ? (
                      <div className='error'>{errors.manager}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='file'
                      className='block text-sm font-semibold leading-6'
                    >
                      Image
                    </label>
                    <input
                      id='file'
                      name='file'
                      type='file'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                      onChange={(event) => {
                        setFieldValue("file", event.currentTarget.files[0]);
                      }}
                    />

                    {errors.file && touched.file ? (
                      <div className='error'>{errors.file}</div>
                    ) : null}
                  </div>
                  <button
                    type='submit'
                    className='block rounded-md bg-rose-400 px-3 py-2 mt-5 text-center text-sm font-semibold text-white shadow-sm hover:bg-rose-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-400'
                  >
                    {isSubmitting ? (
                      <div className='flex flex-row justify-center items-center'>
                        Pending
                        <span className='relative flex h-3 w-3 ml-3'>
                          <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-gray-200 opacity-75'></span>
                          <span className='relative inline-flex rounded-full h-3 w-3 bg-white'></span>
                        </span>
                      </div>
                    ) : (
                      `Save`
                    )}
                  </button>
                  {message && (
                    <p className='text-sm mt-2 text-rose-600'>{message}</p>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={editModalIsOpen}
        // onAfterOpen={afterOpenModal}
        onRequestClose={closeEditModal}
        style={modalStyles}
        contentLabel='Edit Modal'
      >
        <div className='bg-zinc-50 shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                Edit Employee
              </h3>
              <button
                onClick={closeEditModal}
                className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
              >
                Cancel
              </button>
            </div>
            <div className='mt-2 max-w-xl text-sm text-gray-500'>
              <p>Manually edit an employee.</p>
            </div>
            <Formik
              initialValues={{
                firstName: selectedEmployee.firstName,
                lastName: selectedEmployee.lastName,
                phoneNumber: selectedEmployee.phoneNumber,
                mobile: selectedEmployee.mobile,
                email: selectedEmployee.email,
                role: selectedEmployee.role,
                location: selectedEmployee.location,
                department: selectedEmployee.department,
                manager: selectedEmployee.manager,
                file: selectedEmployee.file,
              }}
              validationSchema={schema}
              onSubmit={async (values, actions) => {
                await handleUpdate(values);
                actions.setSubmitting(false);
              }}
            >
              {({ isSubmitting, setFieldValue, errors, touched, values }) => (
                <Form className='mt-5 sm:flex sm:items-center flex-col'>
                  <div className='flex flex-row'>
                    <div className='w-full sm:max-w-xs mr-2'>
                      <label
                        htmlFor='first-name'
                        className='block text-sm font-semibold leading-6'
                      >
                        First name
                      </label>
                      <Field
                        type='text'
                        name='firstName'
                        id='first-name'
                        autoComplete='given-name'
                        className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                      />
                      {errors.firstName && touched.firstName ? (
                        <div className='error'>{errors.firstName}</div>
                      ) : null}
                    </div>
                    <div className='w-full sm:max-w-xs'>
                      <label
                        htmlFor='last-name'
                        className='block text-sm font-semibold leading-6'
                      >
                        Last name
                      </label>
                      <Field
                        type='text'
                        name='lastName'
                        id='last-name'
                        autoComplete='family-name'
                        className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                      />
                      {errors.lastName && touched.lastName ? (
                        <div className='error'>{errors.lastName}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='email'
                      className='block text-sm font-semibold leading-6'
                    >
                      Email
                    </label>
                    <Field
                      type='email'
                      name='email'
                      id='email'
                      autoComplete='email'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.email && touched.email ? (
                      <div className='error'>{errors.email}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='phone-number'
                      className='block text-sm font-semibold leading-6'
                    >
                      Phone
                    </label>
                    <Field
                      type='tel'
                      name='phoneNumber'
                      id='phone-number'
                      autoComplete='tel'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.phoneNumber && touched.phoneNumber ? (
                      <div className='error'>{errors.phoneNumber}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='phone-number'
                      className='block text-sm font-semibold leading-6'
                    >
                      Mobile
                    </label>
                    <Field
                      type='tel'
                      name='mobile'
                      id='phone-number'
                      autoComplete='tel'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.mobile && touched.mobile ? (
                      <div className='error'>{errors.mobile}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='role'
                      className='block text-sm font-semibold leading-6'
                    >
                      role
                    </label>
                    <Field
                      type='text'
                      name='role'
                      id='role'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.role && touched.role ? (
                      <div className='error'>{errors.role}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='location'
                      className='block text-sm font-semibold leading-6'
                    >
                      Location
                    </label>
                    <Field
                      type='text'
                      name='location'
                      id='location'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.location && touched.location ? (
                      <div className='error'>{errors.location}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='department'
                      className='block text-sm font-semibold leading-6'
                    >
                      Department
                    </label>
                    <Field
                      type='text'
                      name='department'
                      id='department'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.department && touched.department ? (
                      <div className='error'>{errors.department}</div>
                    ) : null}
                  </div>
                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='methods'
                      className='block text-sm font-semibold leading-6'
                    >
                      Manager
                    </label>
                    <Field
                      type='text'
                      name='manager'
                      id='manager'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                    />
                    {errors.manager && touched.manager ? (
                      <div className='error'>{errors.methods}</div>
                    ) : null}
                  </div>

                  <div className='w-full sm:max-w-xs'>
                    <label
                      htmlFor='file'
                      className='block text-sm font-semibold leading-6'
                    >
                      Image
                    </label>
                    {selectedEmployee.file && (
                      <img
                        className='w-[50px] h-[50px] mb-5'
                        alt='selected'
                        src={selectedEmployee.file}
                      ></img>
                    )}

                    <input
                      id='file'
                      name='file'
                      type='file'
                      className='block w-full rounded-md pl-[5px] border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm h-[40px] md:text-md'
                      onChange={(event) => {
                        setFieldValue("file", event.currentTarget.files[0]);
                      }}
                    />

                    {errors.file && touched.file ? (
                      <div className='error'>{errors.file}</div>
                    ) : null}
                  </div>

                  <button
                    type='submit'
                    className='block rounded-md bg-rose-400 px-3 py-2 mt-5 text-center text-sm font-semibold text-white shadow-sm hover:bg-rose-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-400'
                  >
                    {isSubmitting ? (
                      <span className='relative flex h-3 w-3'>
                        <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75'></span>
                        <span className='relative inline-flex rounded-full h-3 w-3 bg-sky-500'></span>
                      </span>
                    ) : (
                      `Save`
                    )}
                  </button>
                  {message && (
                    <p className='text-sm mt-2 text-rose-600'>{message}</p>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={detailModalIsOpen}
        onRequestClose={closeDetailModal}
        style={detailModalStyles}
        contentLabel='Detail Modal'
      >
        <div className='bg-zinc-50 h-full shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                {detailEmployee.firstName} {detailEmployee.lastName}
              </h3>
              <div className='flex flex-col space-y-4'>
                <button
                  onClick={closeDetailModal}
                  className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
                >
                  Close
                </button>
                {role.role === "admin" && (
                  <>
                    <button
                      onClick={(e) => {
                        setSelectedEmployee(detailEmployee);
                        closeDetailModal();
                        openEditModal();
                      }}
                      className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        setDetailModalIsOpen(false);
                        setConfirmationIsOpen(true);
                      }}
                      className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className='mt-2 max-w-xl text-sm text-gray-500 mb-5'>
              <p>Details for this employee</p>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
              <div className='w-full sm:max-w-xs'>
                <label
                  htmlFor='detail-email'
                  className='block text-sm font-semibold leading-6'
                >
                  Email
                </label>
                <p className='text-sm text-gray-600'>{detailEmployee.email}</p>
              </div>
              <div className='w-full sm:max-w-xs'>
                <label
                  htmlFor='detail-phone'
                  className='block text-sm font-semibold leading-6'
                >
                  Phone
                </label>
                <p className='text-sm text-gray-600'>
                  {detailEmployee.phoneNumber}
                </p>
                {detailEmployee.mobile && (
                  <>
                    <label
                      htmlFor='detail-mobile'
                      className='block text-sm font-semibold leading-6'
                    >
                      Mobile
                    </label>
                    <p className='text-sm text-gray-600'>
                      {detailEmployee.mobile}
                    </p>
                  </>
                )}
              </div>
              <div className='w-full sm:max-w-xs mr-2'>
                <label
                  htmlFor='detail-handle'
                  className='block text-sm font-semibold leading-6'
                >
                  Location
                </label>
                <p className='text-sm text-gray-600'>
                  {detailEmployee.location}
                </p>
              </div>
              <div className='w-full sm:max-w-xs mr-2'>
                <label
                  htmlFor='detail-handle'
                  className='block text-sm font-semibold leading-6'
                >
                  Department
                </label>
                <p className='text-sm text-gray-600'>
                  {detailEmployee.department}
                </p>
              </div>
              <div className='w-full sm:max-w-xs'>
                <label
                  htmlFor='detail-methods'
                  className='block text-sm font-semibold leading-6'
                >
                  Manager
                </label>
                <p className='text-sm text-gray-600'>
                  {detailEmployee.manager}
                </p>
              </div>

              <div className='w-full sm:max-w-xs'>
                <label
                  htmlFor='detail-role'
                  className='block text-sm font-semibold leading-6'
                >
                  Role
                </label>
                <p className='text-sm text-gray-600'>{detailEmployee.role}</p>
              </div>

              <div className='w-full sm:max-w-xs'>
                <label
                  htmlFor='id'
                  className='block text-sm font-semibold leading-6'
                >
                  ID
                </label>
                <p className='text-sm text-gray-600'>{detailEmployee.id}</p>
              </div>

              {detailEmployee.file && (
                <div className='w-full sm:max-w-xs'>
                  <label
                    htmlFor='detail-methods'
                    className='block text-sm font-semibold leading-6'
                  >
                    Image
                  </label>
                  {detailEmployee.file.includes(".pdf") ? (
                    <a
                      className='text-rose-400 font-semibold hover:text-rose-200'
                      href={detailEmployee.file}
                    >
                      PDF FILE
                    </a>
                  ) : (
                    <>
                      <img alt='license' src={detailEmployee.file} />
                      <a
                        className='text-rose-400 mt-5 text-xs hover:text-rose-200'
                        href={detailEmployee.file}
                      >
                        Download File
                      </a>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={confirmationIsOpen}
        // onAfterOpen={afterOpenModal}
        onRequestClose={toggleConfirmation}
        style={modalStyles}
        contentLabel='Confirmation Modal'
      >
        <div className='bg-white shadow sm:rounded-lg'>
          <div className='px-4 py-5 sm:p-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-medium leading-6 text-gray-900'>
                Delete Employee
              </h3>
              <button
                onClick={toggleConfirmation}
                className='text-red-400 border-[1px] border-red-400 rounded-md px-2 py-1'
              >
                Cancel
              </button>
            </div>
            <div className='mt-2 mb-4 max-w-xl text-sm text-gray-500'>
              <p>Are you sure you want to delete this employee?</p>
            </div>

            <button
              onClick={(e) => handleDelete(detailEmployee.id)}
              className='block rounded-md bg-rose-400 px-3 py-2 mt-5 text-center text-sm font-semibold text-white shadow-sm hover:bg-rose-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-400'
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
